// SP-3 — the TPS setup stepper, a sibling of the Monthly Cycle Cockpit's
// CycleStepper (numbered circles → check when done, connector lines). Step
// states are server-derived (TpsSetupStepDto) — this component never
// re-derives readiness facts. The `waiting` state (the baton is outside
// Phoenix: Stripe verifying the firm, or the firm's contact hasn't set a
// password yet) gets a distinct hourglass/warning tone — deliberately NOT
// the blocked-error tone, nothing is wrong.

import { Check, Hourglass } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import type { TpsSetupStepDto, TpsSetupStepKey } from "@/types/tps";

export const SETUP_STEP_LABEL: Record<TpsSetupStepKey, string> = {
  details: "Entity & contact",
  certified: "Certified",
  connect: "Stripe payment accounts",
  "portal-user": "Portal access",
  "fee-schedule": "Fee schedule",
  ready: "Ready for loans",
};

export function TpsSetupStepper({ steps }: { steps: TpsSetupStepDto[] }) {
  return (
    <ol
      className="flex items-start gap-space-2 overflow-x-auto list-none p-0 m-0"
      aria-label="TPS setup"
      data-testid="tps-setup-stepper"
    >
      {steps.map((step, idx) => {
        const isDone = step.state === "done";
        const isActive = step.state === "active";
        const isWaiting = step.state === "waiting";
        const next = steps[idx + 1];
        return (
          <li
            key={step.key}
            className="flex flex-col items-center text-center gap-space-1 min-w-[96px] shrink-0"
            data-step={step.key}
            data-state={step.state}
            data-testid={`tps-setup-step-${step.key}`}
          >
            <div className="flex items-center w-full">
              <span
                className={cn(
                  "h-px flex-1",
                  idx === 0
                    ? "opacity-0"
                    : isDone || isActive || isWaiting
                      ? "bg-status-success"
                      : "bg-border-light",
                )}
                aria-hidden="true"
              />
              <span
                className={cn(
                  "inline-flex items-center justify-center w-7 h-7 rounded-full border text-caption font-semibold shrink-0",
                  isDone &&
                    "border-status-success bg-status-success/15 text-status-success",
                  isActive && "border-gold bg-gold text-onyx animate-pulse",
                  isWaiting &&
                    "border-status-warning bg-status-warning/15 text-status-warning",
                  step.state === "upcoming" &&
                    "border-border-light bg-white-warm text-gray-mid",
                )}
                aria-current={isActive || isWaiting ? "step" : undefined}
              >
                {isDone ? (
                  <Check size={14} aria-hidden />
                ) : isWaiting ? (
                  <Hourglass size={14} aria-hidden />
                ) : (
                  idx + 1
                )}
              </span>
              <span
                className={cn(
                  "h-px flex-1",
                  idx === steps.length - 1
                    ? "opacity-0"
                    : next && next.state === "done"
                      ? "bg-status-success"
                      : "bg-border-light",
                )}
                aria-hidden="true"
              />
            </div>
            <span
              className={cn(
                "text-caption leading-tight px-space-1",
                isWaiting
                  ? "text-status-warning font-semibold"
                  : isActive
                    ? "text-onyx font-semibold"
                    : isDone
                      ? "text-onyx"
                      : "text-gray-mid",
              )}
            >
              {SETUP_STEP_LABEL[step.key]}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
